import React from "react";
import { makeStyles } from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import clsx from "clsx";
import { getTagColor } from "../../utils/tagColors";

// Renders the DRIVER "RouteTag(s)" (from Odoo) as small color-coded chips.
// The color is derived from the tag name (see utils/tagColors), so the same
// tag shows the same hue on a stop and on the route that carries it — letting
// a dispatcher match a stop to its route at a glance. The name is always
// shown, so color is only an accelerator (never the sole signal).

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
    minWidth: 0
  },
  // Single-line variant for fixed-height rows (e.g. the virtualized unscheduled
  // list) so multiple tags can never wrap and overflow the reserved row height.
  wrapperSingleLine: {
    flexWrap: "nowrap",
    overflow: "hidden"
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: 150,
    padding: "2px 10px 2px 8px",
    borderRadius: 16,
    border: "1px solid transparent",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
    lineHeight: 1.4,
    whiteSpace: "nowrap"
  },
  // When the chip pins/locks the match: it's an affordance, so make it feel clickable.
  clickable: {
    cursor: "pointer",
    "&:hover": {
      filter: "brightness(0.97)"
    }
  },
  // Pinned state: emphasise using the tag's OWN hue (a darker border + the lock
  // icon), never a clashing blue — the border colour is applied inline per-tag.
  locked: {
    fontWeight: 700
  },
  lockIcon: {
    fontSize: theme.typography.pxToRem(12),
    marginLeft: 1,
    flex: "none",
    opacity: 0.8
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flex: "none"
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  }
}));

// Keep only DRIVER-type tags (the RouteTag), deduped by name.
const getRouteTags = tags => {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  const out = [];
  tags.forEach(tag => {
    if (tag && tag.type === "DRIVER" && tag.name && !seen.has(tag.name)) {
      seen.add(tag.name);
      out.push(tag);
    }
  });
  return out;
};

// `onToggle(e)` — when provided, each chip becomes a click-to-pin control
// (the caller stops propagation so the row's own click still opens stop details
// only when the chip itself wasn't clicked). `locked` shows the pinned state.
export default function RouteTagChips({
  tags,
  className,
  singleLine,
  onToggle,
  locked
}) {
  const classes = useStyles();
  const routeTags = getRouteTags(tags);
  if (!routeTags.length) return null;

  const clickable = typeof onToggle === "function";
  const handleClick = e => {
    if (!clickable) return;
    e.stopPropagation();
    onToggle(e);
  };

  return (
    <div
      className={clsx(
        classes.wrapper,
        singleLine && classes.wrapperSingleLine,
        className
      )}
      data-cy="routeTagChips"
    >
      {routeTags.map(tag => {
        const color = getTagColor(tag.name);
        return (
          <span
            key={tag.id || tag.name}
            className={clsx(classes.chip, {
              [classes.clickable]: clickable,
              [classes.locked]: locked
            })}
            title={
              clickable
                ? locked
                  ? `${tag.name} — pinned. Click to unpin.`
                  : `${tag.name} — click to pin matching routes`
                : tag.name
            }
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={
              clickable
                ? e => {
                    if (e.key === "Enter" || e.key === " ") handleClick(e);
                  }
                : undefined
            }
            style={{
              backgroundColor: color.bg,
              color: color.text,
              // Pinned → border in the tag's own dark shade (cohesive, not blue).
              borderColor: locked ? color.text : color.border
            }}
          >
            <span
              className={classes.dot}
              style={{ backgroundColor: color.dot }}
            />
            <span className={classes.label}>{tag.name}</span>
            {locked && <LockIcon className={classes.lockIcon} />}
          </span>
        );
      })}
    </div>
  );
}
